import { useState } from 'react';
import { Plus, X, Zap } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useAuthor } from '@/hooks/useAuthor';
import { genUserName } from '@/lib/genUserName';
import { nip19 } from 'nostr-tools';

export interface ZapSplit {
  npub: string;
  weight: string;
}

interface LightningZapSplitsProps {
  zapSplits: ZapSplit[];
  onZapSplitsChange: (splits: ZapSplit[]) => void;
  artistNpub?: string;
  className?: string;
}

export function LightningZapSplits({
  zapSplits,
  onZapSplitsChange,
  artistNpub = '',
  className = '',
}: LightningZapSplitsProps) {
  const { user } = useCurrentUser();
  const userNpub = user?.pubkey ? nip19.npubEncode(user.pubkey) : '';

  // Decode and fetch artist profile if artistNpub is provided
  let artistPubkey = '';
  try {
    if (artistNpub && artistNpub.startsWith('npub1')) {
      const decoded = nip19.decode(artistNpub);
      if (decoded.type === 'npub') {
        artistPubkey = decoded.data;
      }
    }
  } catch {
    // Invalid npub, ignore
  }

  const artistProfile = useAuthor(artistPubkey);
  const uploaderProfile = useAuthor(user?.pubkey || '');

  // Track whether user wants to use splits (starts false = single recipient)
  const [useSplits, setUseSplits] = useState(zapSplits.length > 1);

  // Calculate total weight
  const totalWeight = zapSplits.reduce(
    (sum, split) => sum + parseInt(split.weight || '0'),
    0
  );

  const handleEnableSplits = () => {
    // If there's an artist npub, start with artist at 100% (weight 1) and uploader at 0%
    // Otherwise, start with uploader at 100% (weight 1) and empty second recipient at 0%
    if (artistNpub) {
      onZapSplitsChange([
        { npub: artistNpub, weight: '1' },
        { npub: userNpub, weight: '0' },
      ]);
    } else {
      onZapSplitsChange([
        { npub: userNpub, weight: '1' },
        { npub: '', weight: '0' },
      ]);
    }
    setUseSplits(true);
  };

  const handleDisableSplits = () => {
    // Reset to single recipient
    onZapSplitsChange([{ npub: '', weight: '1' }]);
    setUseSplits(false);
  };

  const handleAddSplit = () => {
    onZapSplitsChange([...zapSplits, { npub: '', weight: '1' }]);
  };

  const handleRemoveSplit = (index: number) => {
    const newSplits = zapSplits.filter((_, i) => i !== index);

    // If down to 1 split, disable split mode
    if (newSplits.length <= 1) {
      handleDisableSplits();
    } else {
      onZapSplitsChange(newSplits);
    }
  };

  const handleUpdateSplit = (index: number, field: 'npub' | 'weight', value: string) => {
    const newSplits = [...zapSplits];
    newSplits[index][field] = value;
    onZapSplitsChange(newSplits);
  };

  // Calculate percentages for display
  const getPercentage = (weight: string) => {
    const w = parseInt(weight || '0');
    if (totalWeight === 0) return 0;
    return Math.round((w / totalWeight) * 100);
  };

  return (
    <div className={className}>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Label className="flex items-center gap-2">
            <Zap className="h-4 w-4 text-yellow-500" />
            Zap Recipients
          </Label>
          {!useSplits && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleEnableSplits}
              className="gap-2"
            >
              <Plus className="h-4 w-4" />
              Add Splits
            </Button>
          )}
        </div>

        {/* Single recipient mode */}
        {!useSplits && (
          <div className="p-4 rounded-lg border border-white/[0.11] bg-muted/30">
            <div className="space-y-3">
              {artistNpub ? (
                <>
                  <div className="flex items-center gap-3">
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={artistProfile.data?.metadata?.picture} alt="Artist" />
                      <AvatarFallback>
                        {(artistProfile.data?.metadata?.name || genUserName(artistPubkey)).slice(0, 2).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">
                        {artistProfile.data?.metadata?.name || genUserName(artistPubkey)}
                      </div>
                      <div className="text-xs text-muted-foreground">Artist - receives 100% of zaps</div>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Click "Add Splits" to share zaps with the uploader or other collaborators.
                  </p>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-3">
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={uploaderProfile.data?.metadata?.picture} alt="You" />
                      <AvatarFallback>
                        {(uploaderProfile.data?.metadata?.name || genUserName(user?.pubkey || '')).slice(0, 2).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">
                        {uploaderProfile.data?.metadata?.name || 'You'}
                      </div>
                      <div className="text-xs text-muted-foreground">Uploader - receives 100% of zaps</div>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Click "Add Splits" to share zaps with collaborators.
                  </p>
                </>
              )}
            </div>
          </div>
        )}

        {/* Split mode */}
        {useSplits && (
          <div className="space-y-3">
            <div className="space-y-2">
              {zapSplits.map((split, index) => {
                return <RecipientCard
                  key={index}
                  split={split}
                  index={index}
                  totalSplits={zapSplits.length}
                  userNpub={userNpub}
                  artistNpub={artistNpub}
                  percentage={getPercentage(split.weight)}
                  onUpdate={handleUpdateSplit}
                  onRemove={handleRemoveSplit}
                />;
              })}
            </div>

            {/* Add recipient button */}
            {zapSplits.length < 10 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddSplit}
                className="w-full gap-2"
              >
                <Plus className="h-4 w-4" />
                Add Recipient
              </Button>
            )}

            {/* Back to single recipient */}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleDisableSplits}
              className="w-full text-muted-foreground hover:text-foreground"
            >
              Use Single Recipient Instead
            </Button>

            {/* Help text */}
            <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
              <p className="text-xs text-blue-600 dark:text-blue-400">
                <strong>How it works:</strong> 2 recipients with weights 1, 1 means a 50/50%. A weight of 1, 1, 2 means a split of 25%, 25% and 50%. A weight of 0 means that recipient gets nothing. A lightning address is required to get paid.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function RecipientCard({
  split,
  index,
  totalSplits,
  userNpub,
  artistNpub,
  percentage,
  onUpdate,
  onRemove,
}: {
  split: ZapSplit;
  index: number;
  totalSplits: number;
  userNpub: string;
  artistNpub?: string;
  percentage: number;
  onUpdate: (index: number, field: 'npub' | 'weight', value: string) => void;
  onRemove: (index: number) => void;
}) {
  // Check if this is the current user or artist
  const isCurrentUser = split.npub === userNpub;
  const isArtist = split.npub === artistNpub;

  // Decode npub to get pubkey for profile lookup
  let pubkey = '';
  try {
    if (split.npub && split.npub.startsWith('npub1')) {
      const decoded = nip19.decode(split.npub);
      if (decoded.type === 'npub') {
        pubkey = decoded.data;
      }
    }
  } catch {
    // Invalid npub, will show placeholder
  }

  const profile = useAuthor(pubkey);
  const displayName = profile.data?.metadata?.name || (pubkey ? genUserName(pubkey) : '');
  const avatarUrl = profile.data?.metadata?.picture;

  return (
    <div className="space-y-3 p-3 rounded-lg border border-white/[0.11] bg-muted/30">
      {/* Header with recipient number and remove button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">Recipient {index + 1}</span>
          {isArtist && (
            <span className="text-xs text-muted-foreground">(Artist)</span>
          )}
          {isCurrentUser && !isArtist && (
            <span className="text-xs text-muted-foreground">(You)</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">
            {percentage}%
          </span>
          {totalSplits > 1 && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => onRemove(index)}
              className="h-6 w-6"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Profile display (if npub is valid) */}
      {pubkey && (
        <div className="flex items-center gap-3 p-2 rounded-md bg-muted/50">
          <Avatar className="h-8 w-8">
            <AvatarImage src={avatarUrl} alt={displayName} />
            <AvatarFallback>
              {displayName.slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{displayName}</div>
          </div>
        </div>
      )}

      {/* npub input */}
      <Input
        placeholder="npub1..."
        value={split.npub}
        onChange={(e) => onUpdate(index, 'npub', e.target.value)}
        className="font-mono text-sm"
      />

      {/* Weight input */}
      <div className="flex items-center gap-2">
        <Label className="text-xs text-muted-foreground">Weight:</Label>
        <Input
          type="number"
          min="0"
          value={split.weight}
          onChange={(e) => onUpdate(index, 'weight', e.target.value)}
          className="w-20 text-sm"
        />
      </div>
    </div>
  );
}
